/**
 * @copyright 2026 @ DigiNet
 * @author ANHTAI
 * @create 12/06/2026
 * @Example
 */
import { Column } from "devextreme-react/data-grid";
import { Button, ButtonIcon, Checkbox, Col, Row, TextInput, Typography } from "diginet-core-ui/components";
import { makeStyles } from "diginet-core-ui/theme";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as userActions from "../../../actions/user-actions";
import Config from "../../../config";
import GridActionBar from "../../common/action-bar/grid-action-bar-hover";
import { Loading } from "../../common/loading/loadingCore";
import ActionToolbar from "../../common/tool-bar/action-toolbar";
import GridContainer from "../grid-container/grid-container";

const useStyles = makeStyles(theme => ({
    leftGrid: {
        [theme.breakpoints.down("md")]: {
            height: "calc(100vh - 400px)",
        },
    },
    colGridLeft: {
        height: "100%",
        [theme.breakpoints.up("md")]: {
            borderRight: `1px solid ${theme?.colors?.line?.system}`,
        },
    },
    divScroll: {
        height: "calc(100vh - 244px)",
        paddingTop: theme?.spacing(4),
        overflow: "auto",
        [theme.breakpoints.down("md")]: {
            height: "100%",
            overflow: "inherit",
        },
    },
    divBottom: {
        padding: theme.spacing(6),
        marginLeft: -theme.spacing(4),
        marginRight: -theme.spacing(4),
        boxShadow: `0px -1px 0px ${theme?.colors?.line?.system}`,
        position: "relative",
    },
}));

const perPage = 20;

const W2WPages = React.forwardRef((props, ref) => {
    const classes = useStyles();
    const isSubmit = useState(false);
    const [dataGrid, setDataGrid] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("view");
    const [iPermission, setPermission] = useState(4);

    const gridLeft = useRef(null);
    const filter = useRef({
        skip: 0,
        limit: perPage,
        StrSearch: ''
    });

    let {
        FormID,
        children,
        updateMode,
        onSelectedRow,
        leftCol,
        onLoaded,
        onLoadedGrid,
        action,
        actionToolbar,
        onSave,
        typePaging,
        customFilter,
        titleRight,
        gridProps,
        keyExpr,
        disabledSave,
        loading: loadingProps,
        isChanged,
        checkPermission,
    } = props;

    typePaging = typePaging || gridProps?.typePaging;

    useEffect(() => {
        loadPermission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // refresh mode khi nhấn add nhiều lần
        if (mode === "refreshAdd") {
            setMode("add");
            updateMode("add");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        if (iPermission) {
            onLoaded && onLoaded(iPermission);
            loadGrid(null, true, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iPermission]);

    useEffect(() => {
        if (!dataGrid || !onLoadedGrid) return;
        onLoadedGrid(dataGrid?.rows || dataGrid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataGrid]);

    useImperativeHandle(ref, () => ({
        // instance: gridRef.current.instance,
        loadGrid: params => {
            if (gridLeft.current) gridLeft.current.enableFocusRow();
            loadGrid(params, true, true);
        },
        gridRef: gridLeft.current,
        Permission: iPermission
    }));

    const rightCol = {};
    Object.keys(leftCol).forEach(key => {
        if (!leftCol?.[key] || isNaN(leftCol?.[key])) return;
        rightCol[key] = 12 - (['xs', 'sm'].includes(key) ? 0 : Number(leftCol[key]));
    });

    /**
     * Update filter load grid
     * @param page
     * @param per
     */
    const updateFilter = (page, per) => {
        if (page || page === 0) {
            filter.current.skip = page * filter.current.limit;
        } else if (per) {
            filter.current.skip = 0;
            filter.current.limit = per;
        }
        loadGrid && loadGrid(null, false, true);
    };
    /**
     * load permission screen
     */
    const loadPermission = () => {
        if (!checkPermission) setPermission(4);
        else props.userActions.getPermission(Config.profile.UserID, FormID, isPer => setPermission(isPer));
    };

    /**
     *
     * @param params {object} params use in action load grid left.
     * @param isReset {boolean} reload page to first page.
     * @param focusRow [boolean, string] <focusRow> = true => focus row index 0, <focusRow> = string => focus row key is string
     */
    const loadGrid = (params, isReset, focusRow) => {
        if (isReset) {
            filter.current.skip = 0;
            filter.current.limit = perPage;
            if (gridLeft.current) gridLeft.current.pageIndex(0);
        }
        let { skip, limit, Disabled, ...paramsT } = filter.current;
        setLoading(true);
        const callbackSave = (errors, data) => {
            if (errors) {
                Config.popupCore.show("ERROR", errors);
            }
            setLoading(false);
            const dataNew = { total: data.total ?? data.length, rows: data.rows || data };
            if (Config.isEmpty(dataNew?.rows) && Object.hasOwn(gridLeft.current ?? {}, 'resetRowIndex')) gridLeft.current.resetRowIndex();
            setDataGrid(dataNew);
            if ((dataNew.rows?.length) && focusRow) {
                gridLeft.current.enableFocusRow(focusRow);
            }
        };
        paramsT = { ...paramsT, ...params };
        filter.current = { ...filter.current, ...params };
        if (typePaging === "remote") {
            paramsT = { ...paramsT, skip, limit, Disabled };
        }
        if (!Config.isEmpty(paramsT)) {
            if (typePaging !== "remote") {
                setLoading(false);
                filter.current = paramsT;
            }
            action.loadGrid({ ...paramsT }, callbackSave);
        } else {
            action.loadGrid(callbackSave);
        }
    };

    /**
     * Action when click button Save
     * @param isContinue {boolean} status save and continue
     * @param cbSave {function} callback after save
     * @return {boolean}
     * @private
     */
    const _onSave = async (isContinue = false, cbSave, modeSave = mode) => {
        if (isSubmit.current) return;
        isSubmit.current = true;
        const { params, cb } = await onSave?.({ StrSearch: filter.current?.StrSearch, Mode: modeSave }) || {};
        if (!params) {
            isSubmit.current = false;
            cbSave && cbSave(false);
            return false;
        }
        setLoading(true);
        props.action[modeSave](params, (errors, data) => {
            setLoading(false);
            isSubmit.current = false;
            if (errors) {
                Config.popupCore.show("ERROR", errors);
                cbSave && cbSave(false, params);
            } else if (data) {
                if (data.Status === 1) {
                    let message = data.Message;
                    Config.popupCore.show("WARNING", message);
                    cbSave && cbSave(false, params);
                } else {
                    let message;
                    if (mode === "add") message = Config.lang("Luu_thanh_cong");
                    else if (mode === 'copy') message = Config.lang("Sao_chep_thanh_cong");
                    else message = Config.lang("Chinh_sua_thanh_cong");
                    Config.notify.show("success", message, 2000);
                    // await save history => change row
                    const awaitCb = async () => {
                        if (cb) await cb(isContinue, data);
                        return true;
                    };
                    awaitCb() && cbSave && cbSave(true, params);
                }
            }
        });
    };

    const onAdd = () => {
        if (Object.hasOwn(gridLeft.current ?? {}, 'resetRowIndex')) gridLeft.current.resetRowIndex();
        gridLeft.current.checkChangedFocus(null, () => {
            if (mode === "add") setMode("refreshAdd");
            else {
                onSelectedRow && onSelectedRow(null);
                gridLeft.current.focusedRow(-1, "add");
            }
        });
    };

    const reloadDataGrid = params => {
        const dataT = [...dataGrid.rows];
        dataT.forEach((i, index) => {
            if (i[keyExpr] === params[keyExpr]) {
                Object.keys(i).forEach(key => {
                    if (typeof params[key] !== "undefined") dataT[index][key] = params[key];
                    if (typePaging === "remote") {
                        gridLeft.current.instance.cellValue(index, key, params[key]);
                        gridLeft.current.instance.saveEditData();
                    }
                });
            }
        });
        setDataGrid({ ...dataGrid, rows: dataT });
    };

    const onChangeSearch = (e) => {
        const value = e?.value ?? e?.target?.value ?? '';
        filter.current.StrSearch = value;
    };

    if (!iPermission) return null;
    return (
        <div className={'page-container'}>
            <Loading visible={loading} />
            <ActionToolbar {...actionToolbar}>{actionToolbar?.children}</ActionToolbar>
            <Row className={'mgt0'} style={{ marginBottom: -16 }}>
                <Col {...leftCol} className={`${classes.colGridLeft} pdr4x`}>
                    <div className={'mgb4x display_row'}>
                        <Button
                            color={"primary"}
                            viewType={"filled"}
                            startIcon={"AddFilled"}
                            className={'mgr2x'}
                            disabled={iPermission <= 1 || loading || loadingProps}
                            label={Config.lang("Them_moi")}
                            onClick={onAdd}
                        />
                        <TextInput
                            startIcon={'Search'}
                            className={'full_w'}
                            delayOnChange={1}
                            placeholder={Config.lang('Nhap_noi_dung_can_tim')}
                            viewType={'outlined'}
                            onChange={onChangeSearch}
                            value={filter.StrSearch}
                            disabled={loading || loadingProps}
                            onKeyDown={(e) => {
                                if (e?.keyCode === 13) loadGrid();
                            }}
                        />
                    </div>
                    {customFilter}
                    <GridLeft
                        {...props}
                        filter={filter.current}
                        ref={gridLeft}
                        mode={mode}
                        setMode={setMode}
                        updateMode={updateMode}
                        iPermission={iPermission}
                        dataGrid={dataGrid}
                        setDataGrid={setDataGrid}
                        loadGrid={loadGrid}
                        onSaveRow={_onSave}
                        updateFilter={updateFilter}
                        setLoading={setLoading}
                    />
                </Col>
                <Col {...rightCol}>
                    {titleRight && <Typography type={'h1'}>{titleRight}</Typography>}
                    <div className={classes.divScroll}>{children}</div>
                    <div className={"display_row valign-bottom " + classes.divBottom}>
                        <Button
                            color={"info"}
                            disabled={
                                mode === "view" ||
                                iPermission <= 1 ||
                                disabledSave ||
                                loading ||
                                loadingProps ||
                                !isChanged
                            }
                            label={Config.lang("Luu")}
                            viewType={"filled"}
                            startIcon={"save"}
                            onClick={() =>
                                _onSave(false, (isSuccess, params) => {
                                    if (isSuccess) {
                                        setMode("view");
                                        updateMode("view");
                                        if (["add", "copy"].includes(mode)) loadGrid(null, true, typePaging !== "remote" ? params[keyExpr] : true);
                                        else reloadDataGrid(params);
                                    }
                                })
                            }
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );
});

const GridLeft = React.forwardRef((props, ref) => {
    let {
        FormID,
        keyExpr,
        onSelectedRow,
        dataColumnGrid = [],
        isClickUpdateDisabled,
        gridProps = {},
        loadGrid,
        mode,
        setMode,
        updateMode,
        iPermission,
        dataGrid,
        setDataGrid,
        action,
        isChanged,
        onSaveRow,
        typePaging,
        updateFilter,
        gridHeight,
        buttonActionExtra,
        filter: filterProps,
        setLoading,
    } = props;

    typePaging = typePaging || gridProps?.typePaging;
    const classes = useStyles();

    // keep status changed of form right
    const isChangedRef = useRef(false);
    const onSaveRowRef = useRef(null);
    const isSubmit = useRef(false);
    const gridRef = useRef(false);
    const gridContainerRef = useRef(false);
    const enableFocusRow = useRef();
    const focusRowIndex = useRef({});
    const modeRef = useRef(mode);
    const idRowFocusing = useRef();

    const filter = useRef({
        skip: 0,
        limit: 20,
    });

    useEffect(() => {
        modeRef.current = mode;
    }, [mode]);

    useEffect(() => {
        isChangedRef.current = isChanged;
    }, [isChanged]);

    useEffect(() => {
        onSaveRowRef.current = onSaveRow;
    }, [onSaveRow]);

    useImperativeHandle(ref, () => ({
        instance: gridRef.current.instance,
        focusedRow,
        enableFocusRow: _enableFocusRow,
        checkChangedFocus,
        pageIndex,
        resetRowIndex: () => focusRowIndex.current = {}
    }));

    const _enableFocusRow = (flag = true, mode = "view") => {
        enableFocusRow.current = {
            key: flag,
            mode: mode,
        };
    };

    /**
     * Focus on page by index
     * @param index
     */
    const pageIndex = index => {
        if (typePaging !== "remote") gridRef.current.instance.pageIndex(index);
        else {
            if (gridContainerRef.current && gridContainerRef.current.getRefPaging()) {
                gridContainerRef.current.getRefPaging().onChangePage(index);
            }
        }
    };

    /**
     * Focus on row by key or index
     * @param key or index
     * @param modeF
     */
    const focusedRow = (key, modeF) => {
        setMode(modeF || "view");
        const dataSource = gridRef.current.instance.option('dataSource') || [];
        const isSameRow = focusRowIndex.current?.[keyExpr] === dataSource?.[key]?.[keyExpr];
        setTimeout(() => updateMode(modeF || "view", isSameRow), 300);
        if (key === true) key = 0;
        if (Config.typeOf(key, "number")) {
            gridRef.current.instance.option("focusedRowKey", "");
            gridRef.current.instance.option("focusedRowIndex", key);
        } else if (Config.typeOf(key, 'string')) gridRef.current.instance.option("focusedRowKey", key);
    };


    /**
     * Action when change focus row
     * @type {Function}
     */
    const onFocusedRowChanging = useCallback(
        (e, modeF = "view") => {
            if (!e || isSubmit.current) return;
            isSubmit.current = true;
            // index dòng focus mới
            const index = e?.newRowIndex ?? e?.rowIndex;
            // index dòng đang focus
            const indexPrev = e?.prevRowIndex ?? gridRef.current.instance.option("focusedRowIndex");
            // nếu click dòng cũ và giống mode
            if (index === indexPrev && (modeRef.current === modeF || modeRef.current === "edit")) {
                isSubmit.current = false;
                return;
            }
            // check click icon edit
            const copyIcon = e.event?.target?.closest("#copyIcon");
            const editIcon = e.event?.target?.closest("#editIcon");
            if (editIcon) modeF = "edit";
            if (copyIcon) modeF = "copy";
            // check click icon delete
            // const removeIcon = e.event?.target?.closest("#removeIcon");
            // const isUsedIcon = e.event?.target?.closest("#isUsedIcon");
            // if (removeIcon || isUsedIcon) {
            //     idRowFocusing.current = e.rows[indexPrev]?.key;
            //     isSubmit.current = false;
            //     e.cancel = true;
            //     return;
            // }
            const cbChange = isReload => {
                isChangedRef.current = false;
                // nếu có theo tác lưu data thì focusedRowKey vì có thể có thay đổi total data, ko còn là index đã click trước đó
                if (isReload) _enableFocusRow(e.component.cellValue(index, keyExpr), modeF);
                else focusedRow(index, modeF);
            };
            checkChangedFocus(e, cbChange);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isChangedRef.current, mode]
    );

    /**
     * Request to save data if the form is changed
     * @param e
     * @param cbChange
     */
    const checkChangedFocus = useCallback((e, cbChange) => {
        // check form right is changed
        isSubmit.current = false;
        let isContinue = false;
        if (isChangedRef.current) {
            if (e) e.cancel = true;
            let oldMode = modeRef.current;
            Config.popupCore.show({
                type: 'yesno',
                noText: Config.lang('Tiep_tuc')
            }, Config.lang("Du_lieu_chua_duoc_luu_Ban_co_muon_luu_khong"),
                () => {
                    setTimeout(() => {
                        onSaveRowRef.current &&
                            onSaveRowRef.current(false, isSuccess => {
                                if (isSuccess) {
                                    loadGrid();
                                    cbChange(true);
                                }
                            }, oldMode);
                    }, 200);
                },
                () => isContinue = true
                , () => {
                    if (isContinue) return;
                    cbChange(false)
                }
            );
        } else cbChange(false);
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onSaveRowRef.current]
    );

    /**
     * Check paging is changed
     * @param e
     */
    const checkChangedPaging = useCallback(
        async e => {
            if (isSubmit.current) return;
            isSubmit.current = false;
            // check form right is changed
            if (isChangedRef.current && e) {
                e.cancel = await new Promise(resolve => {
                    Config.popupCore.show("YES_NO", Config.lang("Du_lieu_chua_duoc_luu_Ban_co_muon_luu_khong"),
                        () => {
                            setTimeout(() => {
                                onSaveRowRef.current(false, async isSuccess => {
                                    isSubmit.current = false;
                                    if (typePaging !== "remote") loadGrid();
                                    return resolve(!isSuccess);
                                });
                            }, 200);
                        }, () => resolve(false),
                        ePopup => ePopup?.action === "isClose" && resolve(false)
                    );
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onSaveRowRef.current]
    );

    /**
     * Render column Checkbox Disabled
     * @param e {element}
     * @return {xml}
     */
    const renderDisabledColumn = e => {
        return (
            <Checkbox
                className={"valign-middle"}
                checked={!!e?.data?.Disabled}
                onChange={data => {
                    const params = {
                        FormID,
                        [keyExpr]: e?.data?.[keyExpr] || "",
                        Disabled: Number(data?.value),
                    };
                    Config.popupCore.show("YES_NO", Config.lang(`Du_lieu_se_chuyen_ve_${!e?.data?.Disabled && "khong_"}su_dung_ban_co_muon_luu_khong?`),
                        () => action.editDisabled(params, async (errors, data) => {
                            if (errors) {
                                Config.popupCore.show("ERROR", errors);
                                return false;
                            }
                            if (data?.Status === 1) Config.popupCore.show("WARNING", data?.Message);
                            else {
                                let modeName = 'Luu';
                                if (mode === 'edit') modeName = 'Chinh_sua';
                                else if (mode === 'copy') modeName = 'Sao_chep';
                                const message = Config.lang(`${modeName}_thanh_cong`);
                                Config.notify.show("success", message, 2000);
                                loadGrid && loadGrid();
                            }
                        })
                    );
                }}
            />
        );
    };

    /**
     * Render column action <Edit, Delete>
     * @type {Function}
     */
    const renderButtonAction = useCallback(e => {
        if (!e) return null;
        const { Disabled } = e?.data || {};
        const isUsedTitle = !!Disabled ? 'Su_dung' : 'Khong_su_dung';
        return (
            <GridActionBar>
                <div className={"display_row align-center"}>
                    {buttonActionExtra && buttonActionExtra(e)}
                    <ButtonIcon
                        id={"copyIcon"}
                        circular
                        viewBox
                        viewType={"text"}
                        name={"Copy"}
                        title={Config.lang('Sao_chep')}
                        disabled={iPermission <= 2}
                        onClick={() => onFocusedRowChanging(e, "copy")}
                    />
                    <ButtonIcon
                        id={"editIcon"}
                        circular
                        viewBox
                        className={"mgl1x"}
                        viewType={"text"}
                        name={"edit"}
                        title={Config.lang('Chinh_sua')}
                        disabled={iPermission <= 2}
                        onClick={() => onFocusedRowChanging(e, "edit")}
                    />
                    <ButtonIcon
                        id={"isUsedIcon"}
                        circular
                        viewBox
                        className={"mgl1x"}
                        viewType={"text"}
                        name={"Block"}
                        title={Config.lang(isUsedTitle)}
                        disabled={iPermission <= 3}
                        onClick={() => confirmIsUsed(e)}
                    />
                    <ButtonIcon
                        id={"removeIcon"}
                        circular
                        viewBox
                        className={"mgl1x"}
                        viewType={"text"}
                        name={"delete"}
                        title={Config.lang('Xoa')}
                        disabled={iPermission <= 3}
                        onClick={() => confirmDelete(e)}
                    />
                </div>
            </GridActionBar>
        );
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataGrid, mode]
    );

    /**
     * Show pop-up question definitely delete
     * @param e
     */
    const confirmDelete = e => Config.popupCore.show("YES_NO", Config.lang("Ban_co_chac_muon_xoa?"), () => onDelete(e));

    const confirmIsUsed = e => Config.popupCore.show("YES_NO", Config.lang("Ban_co_chac_muon_thuc_hien_hanh_dong_nay?"), () => onIsUsed(e));

    /**
     * Delete the row
     * @param e {element}
     * @param cb {function} callback after delete
     * @return {boolean} return status delete success is fail
     */
    const onDelete = (e, cb) => {
        if (!e) return false;
        const { data: dt } = e.row;
        const params = {
            FormID,
            ...(keyExpr ? { [keyExpr]: dt[keyExpr] } : dt)
        };
        setLoading(true);
        action.delete(params, (errors, data) => {
            setLoading(false);
            if (errors) {
                Config.popupCore.show("ERROR", errors);
                return false;
            } else if (data) {
                if (data.Status === 1) {
                    Config.popupCore.show("WARNING", data.Message);
                    return false;
                } else {
                    Config.notify.show('success', data?.Message || Config.lang('Xoa_thanh_cong'), 2000);
                    const focusKey = gridRef.current.instance.option("focusedRowKey");
                    if (typePaging === "remote") loadGrid && loadGrid();
                    else {
                        e.component.deleteRow(e.rowIndex);
                        e.component.saveEditData();
                        focusRowIndex.current = {};
                        setDataGrid({ total: dataGrid.total - 1, rows: dataGrid.rows.filter(i => i[keyExpr] !== dt[keyExpr]) });
                    }
                    // Nếu xoá dòng đang focus,dau khi xoá thành công thì focus lại dòng dầu tiên
                    if (focusKey === params[keyExpr]) _enableFocusRow(0);
                    cb && cb();
                }
            }
        });
    };

    /**
     * isUsed the row
     * @param e {element}
     * @param cb {function} callback after isUsed
     * @return {boolean} return status isUsed success is fail
     */
    const onIsUsed = (e, cb) => {
        if (!e) return false;
        const { data: dt } = e.row;
        const params = {
            FormID,
            ...(keyExpr ? { [keyExpr]: dt[keyExpr] } : dt)
        };
        setLoading(true);
        action.isUsed(params, (errors, data) => {
            setLoading(false);
            if (errors) {
                Config.popupCore.show("ERROR", errors);
                return false;
            } else if (data) {
                if (data?.Status === 1) {
                    Config.popupCore.show('WARNING', data.Message);
                    return false;
                } else {
                    Config.notify.show('success', data?.Message || Config.lang('Thao_tac_thanh_cong'), 2000);
                    if (typePaging === "remote") loadGrid && loadGrid();
                    else {
                        focusRowIndex.current = {};
                        e.component.cellValue(e.rowIndex, 'Disabled', +!dt?.Disabled);
                        e.component.saveEditData();
                    }
                    cb && cb();
                }
            }
        });
    };

    return useMemo(() => {
        const dataGridMain = dataGrid?.rows || [];;
        let gridPropsPagingRemote = {};
        if (typePaging !== "remote") {
            if (!dataGridMain.length) onSelectedRow(null);
        } else {
            gridPropsPagingRemote = {
                totalItems: dataGrid?.total,
                itemPerPage: filter.current.limit,
                skipPerPage: filter.current.skip,
            };
        }
        return (
            <GridContainer
                reference={ref => (gridRef.current = ref)}
                ref={ref => (gridContainerRef.current = ref)}
                style={{ border: "none" }}
                keyExpr={keyExpr}
                dataSource={dataGridMain}
                noDataText={Config.lang("No_data")}
                selection={{ mode: "none" }}
                typePaging={typePaging}
                showBorders={false}
                focusedRowEnabled
                defaultFocusedRowIndex={-1}
                hoverStateEnabled={true}
                pagerFullScreen={false}
                itemPerPage={20}
                height={gridHeight || Config.getHeightGrid() - 130}
                elementAttr={{
                    class: classes.leftGrid,
                }}
                // filterRow={{
                //     visible: typePaging === "normal",
                //     showOperationChooser: false,
                // }}
                onFocusedRowChanged={e => {
                    const data = e?.row?.data;
                    if (data && onSelectedRow) {
                        focusRowIndex.current = data;
                        if (idRowFocusing.current !== data[keyExpr] && !isChangedRef.current) setTimeout(() => onSelectedRow(data), 300);
                        else idRowFocusing.current = null;
                    }
                }}
                onFocusedRowChanging={e => {
                    if (e.newRowIndex === -1) return;
                    onFocusedRowChanging(e, "view");
                }}
                onContentReady={() => {
                    // nếu có enableFocusRow.current thì mới xử lý
                    if (!enableFocusRow.current || !gridContainerRef.current || !gridContainerRef.current.getRefPaging() || !Config.isEmpty(focusRowIndex.current)) return;
                    // Focus tới dòng đã đánh dấu
                    focusedRow(enableFocusRow.current.key, enableFocusRow.current.mode || "view");
                    // typePaging === 'remote' thì xoá enableFocusRow.current
                    if (typePaging === "remote") enableFocusRow.current = false;
                    // typePaging === 'local'
                    else {
                        // Tìm vị trí của row cần focus có nằm trên page hiện tại
                        const page = gridContainerRef.current.getRefPaging().instance.option("currentPage") + 1;
                        const indexItem = dataGridMain.findIndex(i => i[keyExpr] === enableFocusRow.current.key);
                        const pageItem = Math.ceil(indexItem / filter.current?.limit);

                        // Nếu nằm ở page khác và FocusRow là string( focusedRowKey) thì clear enableFocusRow.current
                        if (!(page !== pageItem && Config.typeOf(enableFocusRow.current.key, "string"))) enableFocusRow.current = false;
                        // Ngược lại đợi xoá enableFocusRow.current trong onChangePage
                    }
                }}
                onChangingPage={checkChangedPaging}
                onChangePage={page => {
                    isSubmit.current = false;
                    isChangedRef.current = false;
                    if (typePaging === "remote") updateFilter(page, null);
                    // typePaging === 'local'
                    else {
                        // Không có enableFocusRow.current tức là bấm qua trang bằng mũi tên trên paging
                        if (!enableFocusRow.current) {
                            focusedRow(-1);
                            enableFocusRow.current = { key: 0, mode: "view" };
                        }
                        // Bị qua trang do focusedRowKey trên onContentReady=> không làm gì hết
                        else enableFocusRow.current = null;
                    }
                }}
                onChangingPerPage={checkChangedPaging}
                onChangePerPage={perPage => {
                    isSubmit.current = false;
                    isChangedRef.current = false;
                    if (typePaging === "remote") updateFilter(null, perPage);
                    else {
                        if (!enableFocusRow.current) {
                            focusedRow(-1);
                            enableFocusRow.current = { key: 0, mode: "view" };
                        } else enableFocusRow.current = null;
                    }
                }}
                editing={{ texts: { confirmDeleteMessage: "" } }}
                {...gridPropsPagingRemote}
                {...gridProps}
            >
                <Column
                    caption={Config.lang("Hanh_dong")}
                    visible={Config.isMobile}
                    alignment={"center"}
                    allowEditing={false}
                    cellRender={e => renderButtonAction(e)}
                />
                {dataColumnGrid?.map((item, idx) => {
                    let propsColCustom = {};
                    if (item.dataField === "Disabled") {
                        propsColCustom.trueText = Config.lang("Co");
                        propsColCustom.falseText = Config.lang("Khong");
                        if (isClickUpdateDisabled) propsColCustom.cellRender = (e, cb) => renderDisabledColumn(e, cb);
                        else {
                            propsColCustom.dataType = "boolean";
                            propsColCustom.calculateCellValue = rowData => !!rowData.Disabled;
                        }
                    }
                    return (
                        <Column key={idx} {...item} {...propsColCustom}>
                            {item.children}
                        </Column>
                    );
                })}

                <Column
                    fixed={true}
                    minWidth={buttonActionExtra ? 150 : 100}
                    fixedPosition={"right"}
                    visible={!Config.isMobile}
                    cellRender={e => renderButtonAction(e)}
                />
            </GridContainer>
        );
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataGrid, filterProps])
});

W2WPages.defaultProps = {
    leftCol: {
        xs: 12,
        sm: 12,
        md: 6,
        lg: 6,
    },
    dataColumnGrid: [],
    isClickUpdateDisabled: false,
    gridProps: {},
    typePaging: "normal",
    disabledSave: false,
    loading: false,
    checkPermission: true,
};

W2WPages.propTypes = {
    mode: PropTypes.string.isRequired,
    FormID: PropTypes.string.isRequired,
    isChanged: PropTypes.bool.isRequired,

    updateMode: PropTypes.func,
    onSelectedRow: PropTypes.func,
    onLoaded: PropTypes.func,
    onLoadedGrid: PropTypes.func,
    onSave: PropTypes.func,
    loadGrid: PropTypes.func,
    setMode: PropTypes.func,
    /** action when focus row, return (dataRow, mode) */
    onSaveRow: PropTypes.func,

    leftCol: PropTypes.object,
    action: PropTypes.object,
    actionToolbar: PropTypes.object,
    gridProps: PropTypes.object,
    children: PropTypes.any,
    keyExpr: PropTypes.string,
    dataColumnGrid: PropTypes.array,

    dataGrid: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    typePaging: PropTypes.oneOf(["default", "normal", "remote"]),

    /** click Checkbox Không sử dụng thì call api update, default is false */
    isClickUpdateDisabled: PropTypes.bool,
    disabledSave: PropTypes.bool,
    loading: PropTypes.bool,
    /** label Checkbox Không sử dụng*/
    checkboxLabel: PropTypes.string,
    /** list button action on Grid Left */
    buttonActionExtra: PropTypes.func,
    checkPermission: PropTypes.bool,
    titleRight: PropTypes.string,
};

export default connect(null, (dispatch) => ({
    userActions: bindActionCreators(userActions, dispatch),
}))(W2WPages);
